import { NgFor, NgIf, NgStyle } from '@angular/common';
import {
  AfterViewInit, Component, ElementRef, EmbeddedViewRef, EventEmitter,
  HostListener, Injector, Input, OnChanges, OnDestroy, Output,
  SimpleChanges, TemplateRef, ViewChild, ViewContainerRef,
} from '@angular/core';
import {
  ControlValueAccessor, FormControl, FormsModule,
  NG_VALUE_ACCESSOR, NgControl, Validators,
} from '@angular/forms';

@Component({
  selector: 'app-detailed-select-input',
  imports: [FormsModule, NgIf, NgFor, NgStyle],
  templateUrl: './detailed-select-input.component.html',
  styleUrl: './detailed-select-input.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DetailedSelectInputComponent,
    },
  ],
  standalone: true,
})
export class DetailedSelectInputComponent
  implements ControlValueAccessor, OnChanges, AfterViewInit, OnDestroy {

  @Input() options: any[] = [];
  @Input() label: string = '';
  @Input() placeholder: string = 'Select';
  @Input() disabled: boolean = false;
  @Input() displayKey: string = 'name';
  @Input() subDisplayKey: string = 'email';
  @Input() valueKey: string = 'id';
  @Input() loading: boolean = false;
  /** Optional external anchor element for dropdown positioning.
   *  Pass the cell renderer's host element when used inside ag-grid to bypass
   *  inner CSS width-resolution issues that make getBoundingClientRect wrong. */
  @Input() positionAnchor?: HTMLElement;

  @Output() valueChange = new EventEmitter<any>();
  @Output() scrolledToBottom = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('inputContainer') inputContainerRef!: ElementRef<HTMLElement>;
  @ViewChild('dropdownTpl') dropdownTpl!: TemplateRef<any>;

  control: any;
  value: any = null;
  searchText: string = '';
  isOpen: boolean = false;
  filteredOptions: any[] = [];
  required_validator = Validators.required;
  dropdownStyle: { [key: string]: string } = {};

  onChange = (_: any) => { };
  onTouched = () => { };

  private scrollListener: (() => void) | null = null;
  private dropdownEmbedRef: EmbeddedViewRef<any> | null = null;

  constructor(
    private injector: Injector,
    private elRef: ElementRef,
    private vcr: ViewContainerRef,
  ) { }

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.filterOptions();
      this.dropdownEmbedRef?.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const ngControl: NgControl | null = this.injector.get(NgControl, null);
      if (ngControl) this.control = ngControl.control as FormControl;
    }, 100);
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
    this.destroyPortal();
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(value: any): void {
    this.value = value;
    const selected = this.options?.find(opt => opt[this.valueKey] == value);
    this.searchText = selected ? selected[this.displayKey] : '';
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  // ── Open / Close ─────────────────────────────────────────────────────────

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen ? this.closeDropdown() : this.openDropdownInternal();
  }

  openDropdown(): void {
    if (this.disabled || this.isOpen) return;
    this.openDropdownInternal();
  }

  private openDropdownInternal(): void {
    this.isOpen = true;
    this.searchText = '';
    this.filterOptions();
    this.updateDropdownPosition();
    this.addScrollListener();
    this.createPortal();
    setTimeout(() => this.searchInputRef?.nativeElement?.focus());
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.removeScrollListener();
    this.destroyPortal();
    if (this.value) {
      const selected = this.options?.find(opt => opt[this.valueKey] == this.value);
      this.searchText = selected ? selected[this.displayKey] : '';
    } else {
      this.searchText = '';
    }
    this.onTouched();
  }

  // ── Portal (append dropdown to document.body to escape ag-grid transforms)

  private createPortal(): void {
    if (this.dropdownEmbedRef) return;
    this.dropdownEmbedRef = this.vcr.createEmbeddedView(this.dropdownTpl);
    this.dropdownEmbedRef.detectChanges();
    this.dropdownEmbedRef.rootNodes.forEach((node: Node) => document.body.appendChild(node));
  }

  private destroyPortal(): void {
    if (!this.dropdownEmbedRef) return;
    this.dropdownEmbedRef.rootNodes.forEach((node: Node) => {
      try { if (node.parentNode) node.parentNode.removeChild(node); } catch { }
    });
    this.dropdownEmbedRef.destroy();
    this.dropdownEmbedRef = null;
  }

  // ── Position ──────────────────────────────────────────────────────────────

  // Returns the visible bounding rect: intersects the element rect with every
  // overflow:hidden/clip ancestor. Needed when the element is inside a clipping
  // container (ag-grid cell) but physically spans a wider area (full row).
  private getVisibleRect(el: HTMLElement): DOMRect {
    const r = el.getBoundingClientRect();
    let left = r.left, top = r.top, right = r.right, bottom = r.bottom;
    let parent = el.parentElement;
    while (parent && parent !== document.documentElement) {
      const s = window.getComputedStyle(parent);
      if (s.overflow === 'hidden' || s.overflowX === 'hidden' ||
          s.overflow === 'clip'   || s.overflowX === 'clip') {
        const pr = parent.getBoundingClientRect();
        left   = Math.max(left,   pr.left);
        top    = Math.max(top,    pr.top);
        right  = Math.min(right,  pr.right);
        bottom = Math.min(bottom, pr.bottom);
      }
      parent = parent.parentElement;
    }
    return new DOMRect(left, top, right - left, bottom - top);
  }

  updateDropdownPosition(): void {
    const anchorEl = this.positionAnchor ?? this.inputContainerRef?.nativeElement;
    if (!anchorEl) return;
    const rect = this.positionAnchor
      ? anchorEl.getBoundingClientRect()
      : this.getVisibleRect(this.inputContainerRef!.nativeElement);
    const dropdownMaxHeight = 260;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    let left = rect.left;
    let width = rect.width;
    if (left + width > window.innerWidth) left = window.innerWidth - width - 8;
    if (left < 0) left = 8;

    if (openUp) {
      this.dropdownStyle = {
        position: 'fixed',
        bottom: (window.innerHeight - rect.top + gap) + 'px',
        left: left + 'px',
        width: width + 'px',
        'max-height': Math.min(dropdownMaxHeight, spaceAbove) + 'px',
        top: 'auto',
      };
    } else {
      this.dropdownStyle = {
        position: 'fixed',
        top: (rect.bottom + gap) + 'px',
        left: left + 'px',
        width: width + 'px',
        'max-height': Math.min(dropdownMaxHeight, spaceBelow) + 'px',
        bottom: 'auto',
      };
    }
    this.dropdownEmbedRef?.detectChanges();
  }

  // ── Scroll / Resize listeners ─────────────────────────────────────────────

  private addScrollListener(): void {
    this.removeScrollListener();
    const handler = () => { if (this.isOpen) this.updateDropdownPosition(); };
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    this.scrollListener = () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }

  private removeScrollListener(): void {
    if (this.scrollListener) { this.scrollListener(); this.scrollListener = null; }
  }

  // ── User interactions ─────────────────────────────────────────────────────

  onSearchInput(): void {
    this.filterOptions();
    this.searchChange.emit(this.searchText);
    if (!this.isOpen) {
      this.isOpen = true;
      this.updateDropdownPosition();
      this.addScrollListener();
      this.createPortal();
    } else {
      this.dropdownEmbedRef?.detectChanges();
    }
  }

  filterOptions(): void {
    if (!this.searchText) {
      this.filteredOptions = [...(this.options || [])];
      return;
    }
    const search = this.searchText.toLowerCase();
    this.filteredOptions = (this.options || []).filter(opt => {
      const main = (opt[this.displayKey] || '').toLowerCase();
      const sub = this.subDisplayKey ? (opt[this.subDisplayKey] || '').toLowerCase() : '';
      return main.includes(search) || sub.includes(search);
    });
  }

  selectOption(option: any): void {
    this.value = option[this.valueKey];
    this.searchText = option[this.displayKey];
    this.isOpen = false;
    this.removeScrollListener();
    this.destroyPortal();
    this.onChange(this.value);
    this.onTouched();
    this.valueChange.emit(option);
  }

  onDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      this.scrolledToBottom.emit();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        this.openDropdownInternal();
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.isOpen) return;
    const target = event.target as Node;
    const inHost = this.elRef.nativeElement.contains(target);
    const inPortal = this.dropdownEmbedRef?.rootNodes.some(
      (n: Node) => n instanceof Element && n.contains(target),
    );
    if (!inHost && !inPortal) this.closeDropdown();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isOpen) this.updateDropdownPosition();
  }
}
